import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { z } from "zod";

export const analyticalContextSchema = z.object({
  range: z.enum(["7d", "30d", "90d"]).default("30d"),
  channel: z
    .enum(["All", "Phone", "Email", "Chat", "Case", "App"])
    .default("All"),
  region: z
    .enum(["All", "UK", "London", "North West", "Scotland"])
    .default("All"),
  product: z
    .enum(["All", "Current Account", "Mobile App", "Delivery", "Insurance"])
    .default("All"),
  segment: z
    .enum([
      "All",
      "New customers",
      "Digital only",
      "Assisted",
      "Vulnerable customers",
    ])
    .default("All"),
  interaction: z
    .enum(["All", "Service", "Complaint", "Sales", "Digital event"])
    .default("All"),
  view: z.string().max(60).default("Executive brief"),
});

export type AnalyticalContext = z.infer<typeof analyticalContextSchema>;
type ContextValue = {
  filters: AnalyticalContext;
  setFilter: <K extends keyof AnalyticalContext>(
    key: K,
    value: AnalyticalContext[K],
  ) => void;
  clearAll: () => void;
  query: string;
  activeCount: number;
};
const Context = createContext<ContextValue | null>(null);

const defaults = analyticalContextSchema.parse({});
function readUrl(): AnalyticalContext {
  const params = Object.fromEntries(
    new URLSearchParams(window.location.search),
  );
  return analyticalContextSchema.catch(defaults).parse(params);
}

export function AnalyticalContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [filters, setFilters] = useState(readUrl);
  useEffect(() => {
    const onPop = () => setFilters(readUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const write = useCallback((next: AnalyticalContext) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(next))
      if (value !== defaults[key as keyof AnalyticalContext])
        params.set(key, value);
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}`,
    );
    setFilters(next);
  }, []);
  const setFilter = useCallback(
    <K extends keyof AnalyticalContext>(key: K, value: AnalyticalContext[K]) =>
      write({ ...filters, [key]: value }),
    [filters, write],
  );
  const clearAll = useCallback(() => write(defaults), [write]);
  const query = useMemo(() => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters))
      if (value !== defaults[key as keyof AnalyticalContext])
        params.set(key, value);
    return params.toString();
  }, [filters]);
  const activeCount = Object.keys(filters).filter(
    (key) =>
      filters[key as keyof AnalyticalContext] !==
      defaults[key as keyof AnalyticalContext],
  ).length;
  return (
    <Context.Provider
      value={{ filters, setFilter, clearAll, query, activeCount }}
    >
      {children}
    </Context.Provider>
  );
}

export function useAnalyticalContext() {
  const value = useContext(Context);
  if (!value) throw new Error("Analytical context is unavailable");
  return value;
}

const options = {
  range: [
    ["7d", "Last 7 days"],
    ["30d", "Last 30 days"],
    ["90d", "Last 90 days"],
  ],
  channel: ["All", "Phone", "Email", "Chat", "Case", "App"],
  region: ["All", "UK", "London", "North West", "Scotland"],
  product: ["All", "Current Account", "Mobile App", "Delivery", "Insurance"],
  segment: [
    "All",
    "New customers",
    "Digital only",
    "Assisted",
    "Vulnerable customers",
  ],
  interaction: ["All", "Service", "Complaint", "Sales", "Digital event"],
} as const;

export function AnalyticalContextBar() {
  const { filters, setFilter, clearAll } = useAnalyticalContext();
  const productIsScoped = filters.product !== "All";
  return (
    <section className="context-bar" aria-label="Analytical context">
      <div className="context-status">
        <strong>Data through 18 Jul 2026, 10:20</strong>
        <span>96.8% analysis coverage</span>
      </div>
      <div className="context-period" aria-label="Period">
        <span>Period</span>
        <strong>Last 30 days</strong>
      </div>
      <label className="context-field context-field--product">
        <span>Product scope</span>
        <select
          aria-label="Product scope"
          value={filters.product}
          onChange={(event) =>
            setFilter(
              "product",
              event.target.value as AnalyticalContext["product"],
            )
          }
        >
          {options.product.map((product) => (
            <option value={product} key={product}>
              {product === "All" ? "All products" : product}
            </option>
          ))}
        </select>
      </label>
      <button
        className="context-clear"
        type="button"
        onClick={clearAll}
        disabled={!productIsScoped}
      >
        {productIsScoped ? "Reset scope" : "All products"}
      </button>
    </section>
  );
}
